/**
 * Functions and types to convert meshes to data types necessary to use with BSP's
 * @module
 */
import { materialCacheKey, type MaterialSrc } from "common/materials/materialSrc";
import { ensureDefined } from "common/utils/assertOfType";
import type { HatchProperties, HatchMaterial } from "geom/hatchTypes";
import { hatchPropertiesNew } from "geom/hatchTypes";
import type { BufferAttribute, BufferGeometry } from "three";
import {
  buildEdgePlane,
  clipLinesOutsideConvexVol,
  extendedDataPolygonBSPNew,
  getVerticesAABB3,
  isDegeneratePolygonBSP,
  polygonBSPNew,
} from "./bsp";
import type { LineBSP, PolygonBSP } from "./bspTypes";
import { BSP_EPSILON } from "./bspTypes";
import { DIST_EPSILON } from "./core";
import { fillHatchLines, fillHatchLinesUv, getHatchAABB2 } from "./hatch";
import { lineBSPNew } from "./lineBSPNew";
import type { Mesh3AndId, Mesh3 } from "./mesh";
import { planeByThreePoints, planeProjectPolyLine3 } from "./plane";
import { planeBSPByThreePoints } from "./planeBSP";
import { polyLine3ByArray } from "./polyLine3";
import type { UVGeneration } from "./uv";
import type { Vec3 } from "./vec3";
import { vec3Dist, vec3TriangleNormal } from "./vec3";

interface MeshNeighbors {
  neighbors: Int32Array;
  normals: Float32Array;
}

function buildMeshNeighbors(mesh: Mesh3, epsilon: number = BSP_EPSILON): MeshNeighbors | null {
  const { tris, verts, norms } = mesh;

  if (norms === undefined) {
    return null;
  }

  const neighbors = new Int32Array(tris.length);
  const normals = new Float32Array(tris.length);

  for (let i = 0, l = tris.length; i < l; i++) {
    neighbors[i] = -1;
  }

  for (let triA = 0, l = tris.length; triA < l; triA += 3) {
    const t1 = tris[triA] * 3;
    const t2 = tris[triA + 1] * 3;
    const t3 = tris[triA + 2] * 3;

    const [nX, nY, nZ] = vec3TriangleNormal(
      verts[t1],
      verts[t1 + 1],
      verts[t1 + 2],
      verts[t2],
      verts[t2 + 1],
      verts[t2 + 2],
      verts[t3],
      verts[t3 + 1],
      verts[t3 + 2],
    );

    normals[triA] = nX;
    normals[triA + 1] = nY;
    normals[triA + 2] = nZ;

    for (let edgeA = 0; edgeA < 3; edgeA++) {
      const indexA1 = tris[triA + edgeA] * 3;
      const indexA2 = tris[triA + ((edgeA + 1) % 3)] * 3;

      const xa1 = verts[indexA1];
      const ya1 = verts[indexA1 + 1];
      const za1 = verts[indexA1 + 2];

      const xa2 = verts[indexA2];
      const ya2 = verts[indexA2 + 1];
      const za2 = verts[indexA2 + 2];

      const nxa1 = norms[indexA1];
      const nya1 = norms[indexA1 + 1];
      const nza1 = norms[indexA1 + 2];

      const nxa2 = norms[indexA2];
      const nya2 = norms[indexA2 + 1];
      const nza2 = norms[indexA2 + 2];

      if (neighbors[triA + edgeA] < 0) {
        for (let triB = triA + 3; triB < l; triB += 3) {
          let edgeFound = false;

          for (let edgeB = 0; edgeB < 3; edgeB++) {
            const indexB1 = tris[triB + edgeB] * 3;
            const indexB2 = tris[triB + ((edgeB + 1) % 3)] * 3;

            const xb1 = verts[indexB1];
            const yb1 = verts[indexB1 + 1];
            const zb1 = verts[indexB1 + 2];

            const xb2 = verts[indexB2];
            const yb2 = verts[indexB2 + 1];
            const zb2 = verts[indexB2 + 2];

            const nxb1 = norms[indexB1];
            const nyb1 = norms[indexB1 + 1];
            const nzb1 = norms[indexB1 + 2];

            const nxb2 = norms[indexB2];
            const nyb2 = norms[indexB2 + 1];
            const nzb2 = norms[indexB2 + 2];

            const edgeFound1 =
              vec3Dist(xa1, ya1, za1, xb1, yb1, zb1) <= epsilon &&
              vec3Dist(xa2, ya2, za2, xb2, yb2, zb2) <= epsilon &&
              vec3Dist(nxa1, nya1, nza1, nxb1, nyb1, nzb1) <= epsilon &&
              vec3Dist(nxa2, nya2, nza2, nxb2, nyb2, nzb2) <= epsilon;

            const edgeFound2 =
              vec3Dist(xa1, ya1, za1, xb2, yb2, zb2) <= epsilon &&
              vec3Dist(xa2, ya2, za2, xb1, yb1, zb1) <= epsilon &&
              vec3Dist(nxa1, nya1, nza1, nxb2, nyb2, nzb2) <= epsilon &&
              vec3Dist(nxa2, nya2, nza2, nxb1, nyb1, nzb1) <= epsilon;

            if (edgeFound1 || edgeFound2) {
              neighbors[triA + edgeA] = triB / 3;
              neighbors[triB + edgeB] = triA / 3;
              edgeFound = true;
              break;
            }
          }

          if (edgeFound) {
            break;
          }
        }
      }
    }
  }

  return { neighbors, normals };
}

function buildGeometryNeighbors(geometry: BufferGeometry): Int32Array {
  // If index is undefined, the geometry consists of unrelated triangles and we can't build neighbors.
  const indexArray = geometry.index?.array;
  if (indexArray === undefined) {
    return new Int32Array(0);
  }

  const posAttribute: BufferAttribute = geometry.getAttribute("position") as BufferAttribute;
  if (!(posAttribute.isBufferAttribute)) {
    console.assert(false);
  }
  const vertexArray = posAttribute.array;
  const vertexItemSize = posAttribute.itemSize;

  const neighborCount = indexArray.length;
  const neighbors = new Int32Array(neighborCount);

  for (let i = 0; i < neighborCount; i++) {
    neighbors[i] = -1;
  }

  for (let triA = 0, l = indexArray.length; triA < l; triA += 3) {
    const edgeIndicesA = [indexArray[triA], indexArray[triA + 1], indexArray[triA + 2]];

    for (let edgeA = 0; edgeA < 3; edgeA++) {
      const nextEdgeA = (edgeA + 1) % 3;
      const indexA1 = edgeIndicesA[edgeA] * vertexItemSize;
      const indexA2 = edgeIndicesA[nextEdgeA] * vertexItemSize;

      const [xa1, ya1, za1] = [vertexArray[indexA1], vertexArray[indexA1 + 1], vertexArray[indexA1 + 2]];
      const [xa2, ya2, za2] = [vertexArray[indexA2], vertexArray[indexA2 + 1], vertexArray[indexA2 + 2]];

      if (neighbors[triA + edgeA] < 0) {
        for (let triB = triA + 3; triB < l; triB += 3) {
          let edgeFound = false;

          const edgeIndicesB = [indexArray[triB], indexArray[triB + 1], indexArray[triB + 2]];

          for (let edgeB = 0; edgeB < 3; edgeB++) {
            const nextEdgeB = (edgeB + 1) % 3;
            const indexB1 = edgeIndicesB[edgeB] * vertexItemSize;
            const indexB2 = edgeIndicesB[nextEdgeB] * vertexItemSize;

            const [xb1, yb1, zb1] = [vertexArray[indexB1], vertexArray[indexB1 + 1], vertexArray[indexB1 + 2]];
            const [xb2, yb2, zb2] = [vertexArray[indexB2], vertexArray[indexB2 + 1], vertexArray[indexB2 + 2]];

            const edgeFound1 =
              vec3Dist(xa1, ya1, za1, xb1, yb1, zb1) <= DIST_EPSILON &&
              vec3Dist(xa2, ya2, za2, xb2, yb2, zb2) <= DIST_EPSILON;

            const edgeFound2 =
              vec3Dist(xa1, ya1, za1, xb2, yb2, zb2) <= DIST_EPSILON &&
              vec3Dist(xa2, ya2, za2, xb1, yb1, zb1) <= DIST_EPSILON;

            if (edgeFound1 || edgeFound2) {
              neighbors[triA + edgeA] = triB;
              neighbors[triB + edgeB] = triA;
              edgeFound = true;
              break;
            }
          }

          if (edgeFound) {
            break;
          }
        }
      }
    }
  }

  return neighbors;
}

interface BuildPolygonsFromMeshOptions {
  mesh: Mesh3AndId;
  designMaterialId?: MaterialSrc;
  hatchMaterial?: HatchMaterial;
  useMeshUv: boolean;
  propId?: string;
  uvGen?: UVGeneration;
  priority?: number;
  polygons?: PolygonBSP[];
}

export function buildPolygonsFromMesh({
  mesh,
  designMaterialId,
  hatchMaterial,
  useMeshUv,
  propId,
  uvGen,
  priority = 0,
  polygons = [],
}: BuildPolygonsFromMeshOptions): PolygonBSP[] {
  let id = polygons.length;

  const designMaterialKey = designMaterialId ? materialCacheKey(designMaterialId) : undefined;

  const neighbors = buildMeshNeighbors(mesh.mesh);
  if (!neighbors) {
    return [];
  }
  const { neighbors: triangleNeigbors, normals: triangleNormals } = neighbors;
  const {
    mesh: { tris, verts, uvs1 },
    id: entityId,
  } = mesh;
  // Mesh UVs are usable only when the uv array covers every vertex.
  const meshUvs = useMeshUv && uvs1 && uvs1.length * 3 >= verts.length * 2 ? uvs1 : undefined;

  for (let i = 0, l = tris.length; i < l; i += 3) {
    const i1 = tris[i];
    const i2 = tris[i + 1];
    const i3 = tris[i + 2];

    const t1 = i1 * 3;
    const t2 = i2 * 3;
    const t3 = i3 * 3;

    const x1 = verts[t1];
    const y1 = verts[t1 + 1];
    const z1 = verts[t1 + 2];

    const x2 = verts[t2];
    const y2 = verts[t2 + 1];
    const z2 = verts[t2 + 2];

    const x3 = verts[t3];
    const y3 = verts[t3 + 1];
    const z3 = verts[t3 + 2];

    let u1 = 0;
    let v1 = 0;
    let u2 = 0;
    let v2 = 0;
    let u3 = 0;
    let v3 = 0;
    if (meshUvs) {
      u1 = meshUvs[i1 * 2];
      v1 = meshUvs[i1 * 2 + 1];
      u2 = meshUvs[i2 * 2];
      v2 = meshUvs[i2 * 2 + 1];
      u3 = meshUvs[i3 * 2];
      v3 = meshUvs[i3 * 2 + 1];
    }

    const plane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

    if (plane) {
      const { nX, nY, nZ } = plane;
      const edgePlanes = [
        ensureDefined(buildEdgePlane(x1, y1, z1, x2, y2, z2, nX, nY, nZ)),
        ensureDefined(buildEdgePlane(x2, y2, z2, x3, y3, z3, nX, nY, nZ)),
        ensureDefined(buildEdgePlane(x3, y3, z3, x1, y1, z1, nX, nY, nZ)),
      ];

      const neighborNormals: Vec3[] = [];
      for (let edge = 0; edge < 3; edge++) {
        const neighbor = triangleNeigbors[i + edge];

        if (neighbor >= 0) {
          const neighbor3 = neighbor * 3;

          neighborNormals.push([
            triangleNormals[neighbor3],
            triangleNormals[neighbor3 + 1],
            triangleNormals[neighbor3 + 2],
          ]);
        } else {
          neighborNormals.push([-nX, -nY, -nZ]);
        }
      }

      let hatchLines: LineBSP[] = [];
      let hatchProperties: HatchProperties | undefined = undefined;

      if (hatchMaterial) {
        const facePlane = planeByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);
        if (facePlane) {
          let unclippedHatchLines: LineBSP[] | null = null;
          if (meshUvs) {
            unclippedHatchLines = fillHatchLinesUv(
              hatchMaterial,
              [x1, y1, z1, x2, y2, z2, x3, y3, z3],
              [u1, v1, u2, v2, u3, v3],
            );
          }
          if (unclippedHatchLines === null) {
            // Mesh UVs are absent or unusable for this triangle; use generated UVs.
            const loop3 = polyLine3ByArray([x1, y1, z1, x2, y2, z2, x3, y3, z3, x1, y1, z1]);
            const loop2 = planeProjectPolyLine3(facePlane, loop3);
            const { uvPlane, aabb2 } = getHatchAABB2({ plane: facePlane, loop: loop2, uvGen });
            unclippedHatchLines = fillHatchLines(hatchMaterial, uvPlane, facePlane, aabb2);
          }

          hatchLines = clipLinesOutsideConvexVol(unclippedHatchLines, edgePlanes, false);

          hatchProperties = hatchPropertiesNew(facePlane, uvGen, designMaterialId, designMaterialKey, hatchMaterial);
        }
      }

      const vertices = [x1, y1, z1, x2, y2, z2, x3, y3, z3];

      const extendedData = extendedDataPolygonBSPNew(
        id,
        getVerticesAABB3(vertices),
        [lineBSPNew(x1, y1, z1, x2, y2, z2), lineBSPNew(x2, y2, z2, x3, y3, z3), lineBSPNew(x3, y3, z3, x1, y1, z1)],
        [],
        hatchLines,
        neighborNormals,
        edgePlanes,
        hatchProperties,
        entityId,
        propId,
      );

      const polygonBSP = polygonBSPNew(priority, vertices, plane, extendedData);

      if (!isDegeneratePolygonBSP(polygonBSP)) {
        polygons.push(polygonBSP);
        id++;
      }
    }
  }

  return polygons;
}

interface BuildPolygonsFromMeshesOptions {
  meshes: Mesh3AndId[];
  priority?: number;
  polygons?: PolygonBSP[];
}

export function buildPolygonsFromMeshes({
  meshes,
  priority = 0,
  polygons = [],
}: BuildPolygonsFromMeshesOptions): PolygonBSP[] {
  for (let meshIndex = 0, meshCount = meshes.length; meshIndex < meshCount; meshIndex++) {
    buildPolygonsFromMesh({
      mesh: meshes[meshIndex],
      priority,
      polygons,
      useMeshUv: false,
    });
  }

  return polygons;
}

export function buildPolygonsFromGeometry(
  geometry: BufferGeometry,
  priority: number,
  polygons: PolygonBSP[] = [],
): PolygonBSP[] {
  let id = polygons.length;

  const triangleNeigbors = buildGeometryNeighbors(geometry);

  const indexArray = geometry.index ? (i: number) => ensureDefined(geometry.index).array[i] : (i: number) => i;
  const posAttribute: BufferAttribute = geometry.getAttribute("position") as BufferAttribute;
  if (!(posAttribute.isBufferAttribute)) {
    console.assert(false);
  }
  const vertexArray = posAttribute.array;
  const vertexItemSize = posAttribute.itemSize;
  const l = geometry.index ? ensureDefined(geometry.index).array.length : posAttribute.count;
  for (let i = 0; i < l; i += 3) {
    const aVertIndex = indexArray(i) * vertexItemSize;
    const bVertIndex = indexArray(i + 1) * vertexItemSize;
    const cVertIndex = indexArray(i + 2) * vertexItemSize;
    const [x1, y1, z1] = [vertexArray[aVertIndex], vertexArray[aVertIndex + 1], vertexArray[aVertIndex + 2]];
    const [x2, y2, z2] = [vertexArray[bVertIndex], vertexArray[bVertIndex + 1], vertexArray[bVertIndex + 2]];
    const [x3, y3, z3] = [vertexArray[cVertIndex], vertexArray[cVertIndex + 1], vertexArray[cVertIndex + 2]];

    const plane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

    if (plane) {
      const { nX, nY, nZ } = plane;
      const edgePlanes = [
        ensureDefined(buildEdgePlane(x1, y1, z1, x2, y2, z2, nX, nY, nZ)),
        ensureDefined(buildEdgePlane(x2, y2, z2, x3, y3, z3, nX, nY, nZ)),
        ensureDefined(buildEdgePlane(x3, y3, z3, x1, y1, z1, nX, nY, nZ)),
      ];

      const neighborNormals: Vec3[] = [];
      for (let edge = 0; edge < 3; edge++) {
        const neighbor = triangleNeigbors.length > i + edge ? triangleNeigbors[i + edge] : -1;
        let nnX = -nX;
        let nnY = -nY;
        let nnZ = -nZ;

        if (neighbor >= 0) {
          // We compute the normal instead of taking it from the vertex data, so that the plane normal and neighbor normals are computed the same way.
          const triStartIndex = Math.floor(neighbor / 3) * 3;
          const aNeighborVertIndex = indexArray(triStartIndex) * vertexItemSize;
          const bNeighborVertIndex = indexArray(triStartIndex + 1) * vertexItemSize;
          const cNeighborVertIndex = indexArray(triStartIndex + 2) * vertexItemSize;
          const xn1 = vertexArray[aNeighborVertIndex];
          const yn1 = vertexArray[aNeighborVertIndex + 1];
          const zn1 = vertexArray[aNeighborVertIndex + 2];
          const xn2 = vertexArray[bNeighborVertIndex];
          const yn2 = vertexArray[bNeighborVertIndex + 1];
          const zn2 = vertexArray[bNeighborVertIndex + 2];
          const xn3 = vertexArray[cNeighborVertIndex];
          const yn3 = vertexArray[cNeighborVertIndex + 1];
          const zn3 = vertexArray[cNeighborVertIndex + 2];
          const normalPlane = planeBSPByThreePoints(xn1, yn1, zn1, xn2, yn2, zn2, xn3, yn3, zn3);
          if (normalPlane) {
            nnX = normalPlane.nX;
            nnY = normalPlane.nY;
            nnZ = normalPlane.nZ;
          }
        }
        neighborNormals.push([nnX, nnY, nnZ]);
      }

      const vertices = [x1, y1, z1, x2, y2, z2, x3, y3, z3];

      const extendedData = extendedDataPolygonBSPNew(
        id,
        getVerticesAABB3(vertices),
        [lineBSPNew(x1, y1, z1, x2, y2, z2), lineBSPNew(x2, y2, z2, x3, y3, z3), lineBSPNew(x3, y3, z3, x1, y1, z1)],
        [],
        [],
        neighborNormals,
        edgePlanes,
      );

      const polygonBSP = polygonBSPNew(priority, vertices, plane, extendedData);

      if (!isDegeneratePolygonBSP(polygonBSP)) {
        polygons.push(polygonBSP);
        id++;
      }
    }
  }

  return polygons;
}
